/**
 * I18n Provider Component
 * React Context provider for i18n service
 * Handles language state and HTML attribute management
 * @module @infra/i18n
 */

import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import type { Language } from './i18n.types';
import { getI18nService } from './i18n';

interface I18nContextType {
  language: Language;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

interface I18nProviderProps {
  children: ReactNode;
}

const i18nService = getI18nService();

/**
 * I18nProvider component
 * Wraps app to provide i18n context and set up HTML lang attribute
 */
export function I18nProvider({ children }: I18nProviderProps) {
  const [language, setLanguage] = React.useState<Language>(i18nService.getLanguage());

  useEffect(() => {
    // Initialize translations on mount
    i18nService.initialize().catch((error) => {
      console.error('Failed to initialize i18n:', error);
    });
  }, []);

  // `<html lang>` is written by `I18nService.updateHTMLAttributes`, which sets the
  // BCP-47 tag rather than the internal code — deliberately. This provider used to write
  // the internal code here on every language change, landing a tick later and undoing
  // that every time. One writer only.

  useEffect(() => {
    const handleLanguageChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ language: Language }>;
      setLanguage(customEvent.detail.language);
    };

    window.addEventListener('i18n:languageChange', handleLanguageChange);

    return () => {
      window.removeEventListener('i18n:languageChange', handleLanguageChange);
    };
  }, []);

  return <I18nContext.Provider value={{ language }}>{children}</I18nContext.Provider>;
}

/**
 * useI18nContext hook
 * Access i18n context (current language)
 */
export function useI18nContext(): I18nContextType {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useI18nContext must be used within I18nProvider');
  }
  return context;
}
